import type { AxiosError } from 'axios';
import { Box, Button, Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import type { Contract, Offer } from '@/api/generated/models';
import {
  getGetContractQueryKey,
  getListContractsQueryKey,
  useCreateContract,
  useCreateContractFromOfferByClient,
  useCreateFromOffer,
  useGetContract,
  useListContracts,
  useUpdateContract
} from '@/api/generated/contracts/contracts';
import { ContractContractType } from '@/api/generated/models/contractContractType';
import { ContractStatus } from '@/api/generated/models/contractStatus';
import { OfferStatus } from '@/api/generated/models/offerStatus';
import { useListObjects } from '@/api/generated/objects/objects';
import { useGetOffer, useListOfferLines, useListOffers } from '@/api/generated/offers/offers';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import { useListPriceLists } from '@/api/generated/price-lists/price-lists';
import { useListUnits } from '@/api/generated/units/units';
import {
  BackButton,
  ConfirmActionModal,
  ContentCard,
  FormActions,
  MutationButton,
  NumberField,
  PageHeading,
  SelectField,
  tableRowStyle,
  TextareaField,
  TextField,
  Toast,
  WeekPickerField
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { findUnitLabel } from '@/features/units/unitDisplay';
import { dateToIsoWeek, formatAmount, isoWeekToDate, toIsoDateString } from '@/utils/format';

const PAYMENT_TERMS_OPTIONS = ['15', '30', '45', '60', '90'];

interface FormValues {
  clientOrgId: string;
  offerId: string;
  objectId: string;
  priceListId: string;
  contractNumber: string;
  name: string;
  description: string;
  startWeek: string;
  startYear: string;
  endWeek: string;
  endYear: string;
  contractType: string;
  paymentTerms: string;
  coordinator: string;
  additionalWorkPrice: string;
  billingLabel: string;
  ourReference: string;
  vatEnabled: boolean;
  vatRate: string;
}

const emptyValues: FormValues = {
  clientOrgId: '',
  offerId: '',
  objectId: '',
  priceListId: '',
  contractNumber: '',
  name: '',
  description: '',
  startWeek: '',
  startYear: '',
  endWeek: '',
  endYear: '',
  contractType: ContractContractType.FIXED,
  paymentTerms: '30',
  coordinator: '',
  additionalWorkPrice: '',
  billingLabel: '',
  ourReference: '',
  vatEnabled: false,
  vatRate: '25'
};

// Isti obrazac kao OfferFormPage — backend već vraća specifičan, lokalizirani razlog.
const contractErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

/**
 * Puna stranica za kreiranje I uređivanje ugovora (isti obrazac kao
 * OfferFormPage) — zamjena za bivši ContractFormModal popup i
 * CreateContractFromOfferModal (sada objedinjeno ovdje preko "PONUDA"
 * polja). Ako se odabere ponuda, klijent/cjenik se preuzimaju iz nje i VIŠE
 * NISU promjenjivi (prikazani kao onemogućeno polje), dok se naziv/opis/rok
 * plaćanja KOPIRAJU u formu kao početne, i dalje editabilne vrijednosti
 * (backend createFromOffer prima override za svako od njih, vidi
 * CreateContractFromOfferRequest) — partner ih može prilagoditi prije
 * spremanja. Promjena odabrane ponude reinicijalizira sva ta polja na
 * vrijednosti nove ponude; uklanjanje ponude (clear) ih sve resetira na
 * prazno/ručni unos. Stavke ponude se uvijek kopiraju u ugovor pri
 * spremanju. Bez ponude, klijent/cjenik se biraju ručno.
 *
 * Objekt je POSEBAN slučaj PRI KREIRANJU — nikad se ne preuzima od ponude
 * (ponuda nosi samo slobodan tekst, vidi OfferFormPage), nego ga UVIJEK
 * bira sam onaj tko kreira ugovor (dropdown, obavezno polje), bez obzira
 * dolazi li iz ponude (partner ili klijent/CON-09) ili je ugovor samostalan
 * — identitet "tko je odgovoran za objekt/razradu prostora" je uvijek
 * kreator retka (vidi backend ContractService.createDraftFromOffer()).
 *
 * U edit modu (postojeći DRAFT/REJECTED ugovor): naziv/opis/period/rok
 * plaćanja/koordinator/itd. su uvijek ispravljivi. Broj ugovora je uvijek
 * ispravljiv. Klijent/objekt/cjenik su ispravljivi SAMO dok je ugovor DRAFT
 * (vidi canEditClientObject) — nakon REJECTED ostaju zaključani (identitet
 * posla se ne mijenja, isti obrazac kao OfferFormPage, OFF-NEW-55 dopuna), a
 * OSIM TOGA i cijelo vrijeme dok je ugovor nastao iz ponude (contract.offerId),
 * gdje klijent/objekt/cjenik ostaju zaključani (vidi isLinkedToOffer) dok god
 * ta veza postoji — bez obzira na status (backend ContractService.update()
 * baca ContractLinkedToOfferException na bilo koju promjenu klijenta/objekta/
 * cjenika dok god je offerId nepromijenjen i postavljen, čak i u DRAFT-u).
 * Vezu na ponudu je moguće UKLONITI (gumb "Ukloni ponudu", lokalno stanje
 * offerRemoved) — to odmah oslobađa klijent/objekt za ručni odabir (polja
 * se resetiraju na prazno) i pri spremanju šalje offerId=null (backend
 * ContractService.update prihvaća SAMO prijelaz postojeći→null, ne
 * zamjenu/dodavanje druge ponude). Stavke ugovora pritom ostaju netaknute —
 * one su već kopirane u contract_lines pri kreiranju, neovisno o offerId
 * zaglavlju.
 */
export const ContractFormPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const [removeOfferModalOpen, setRemoveOfferModalOpen] = useState(false);
  // Korisnik je uklonio ponudu tijekom OVE edit sesije — klijent/objekt/cjenik postaju slobodno
  // ispravljivi (vidi isLinkedToOffer). Lokalno stanje forme, ne šalje se dok korisnik ne spremi.
  const [offerRemoved, setOfferRemoved] = useState(false);

  const { data: contractData, isLoading: isLoadingContract } = useGetContract(id ?? '', {
    query: { enabled: isEdit, refetchOnMount: 'always' }
  });
  const contract = contractData as Contract | undefined;

  const { data: linkedOfferData } = useGetOffer(contract?.offerId ?? '', {
    query: { enabled: isEdit && !!contract?.offerId }
  });
  const linkedOfferForEdit = linkedOfferData as Offer | undefined;

  const { data: connectionsData } = useListOrganisationConnections();
  const { data: priceListsData } = useListPriceLists();
  const { data: objectsData } = useListObjects();
  const { data: offersData } = useListOffers({ query: { enabled: !isEdit } });
  const { data: contractsData } = useListContracts({ query: { enabled: !isEdit } });
  const { data: unitsData } = useListUnits();

  const clientOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive)
        .map((object) => ({
          value: object.id ?? '',
          label: formatObjectLabel(object)
        }))
        .filter((option) => option.value),
    [objectsData, t]
  );

  const priceListOptions = useMemo(
    () =>
      (priceListsData ?? [])
        .filter((pl) => pl.isActive)
        .map((pl) => ({ value: pl.id ?? '', label: pl.name ?? '—' })),
    [priceListsData]
  );

  // Ponuda smije postati izvor ugovora samo ako je PRIHVAĆENA i još nema
  // svoj ugovor (backend uq_contracts_offer dopušta najviše jedan).
  const usedOfferIds = useMemo(
    () => new Set((contractsData ?? []).map((c) => c.offerId).filter(Boolean)),
    [contractsData]
  );
  const offerOptions = useMemo(
    () =>
      (offersData ?? [])
        .filter((offer) => offer.status === OfferStatus.ACCEPTED && !usedOfferIds.has(offer.id))
        .map((offer) => ({ value: offer.id ?? '', label: `#${offer.offerNumber ?? '—'}` }))
        .filter((option) => option.value),
    [offersData, usedOfferIds]
  );

  const form = useForm<FormValues>({
    initialValues: emptyValues,
    validate: (values) => {
      const required = t('validation.required');
      const errors: Partial<Record<keyof FormValues, string>> = {};
      if (!isEdit) {
        if (!values.contractNumber.trim()) errors.contractNumber = required;
        if (!values.offerId && !values.clientOrgId) errors.clientOrgId = required;
        // Tko god kreira ugovor (iz ponude, klijent iz CON-09, ili samostalno) uvijek sam bira
        // VLASTITI objekt — objectId je obavezan u sva tri slučaja (vidi gore).
        if (!values.objectId) {
          errors.objectId = required;
        }
        if (!values.name.trim()) errors.name = required;
        if (!values.paymentTerms) errors.paymentTerms = required;
      } else {
        if (!values.name.trim()) errors.name = required;
        if (!values.paymentTerms) errors.paymentTerms = required;
        // Broj ugovora je uvijek ispravljiv. Klijent/objekt su ispravljivi samo dok ugovor NIJE
        // nastao iz ponude (vidi isLinkedToOffer) — inače su onemogućena, read-only polja.
        if (!values.contractNumber.trim()) errors.contractNumber = required;
        if (!(isEdit && !!contract?.offerId) || offerRemoved) {
          if (!values.clientOrgId) errors.clientOrgId = required;
          if (!values.objectId) errors.objectId = required;
        }
      }
      if (values.startWeek && values.startYear && values.endWeek && values.endYear) {
        const start = isoWeekToDate(Number(values.startWeek), Number(values.startYear));
        const end = isoWeekToDate(Number(values.endWeek), Number(values.endYear));
        if (start && end && start.getTime() > end.getTime()) {
          errors.endWeek = t('createContract.startAfterEndError');
        }
      }
      return errors;
    }
  });

  // Prati je li korisnik SAM dirao polje (ne samo "je li trenutno prazno") — isključivo "je
  // li prazno" je bio bug (CON-NEW-07): nakon odabira ponude, auto-popuna predloška broja
  // ugovora bi se znala okinuti i PREKO već upisane vrijednosti ako je taj drugi efekt (koji
  // postavlja objectId/priceListId/name/...) ponovno prošao prije nego je React stigao
  // zabilježiti korisnikov unos u `form.values`. Ref je sinkron i imun na taj redoslijed.
  const contractNumberTouchedRef = useRef(false);

  useEffect(() => {
    if (isEdit && contract) {
      const start = contract.startDate ? dateToIsoWeek(new Date(contract.startDate)) : undefined;
      const end = contract.endDate ? dateToIsoWeek(new Date(contract.endDate)) : undefined;
      form.setValues({
        clientOrgId: contract.clientOrgId ?? '',
        offerId: contract.offerId ?? '',
        objectId: contract.objectId ?? '',
        priceListId: contract.priceListId ?? '',
        contractNumber: contract.contractNumber ?? '',
        name: contract.name ?? '',
        description: contract.description ?? '',
        startWeek: start ? String(start.week) : '',
        startYear: start ? String(start.year) : '',
        endWeek: end ? String(end.week) : '',
        endYear: end ? String(end.year) : '',
        contractType: contract.contractType ?? ContractContractType.FIXED,
        paymentTerms: String(contract.paymentTerms ?? 30),
        coordinator: contract.coordinator ?? '',
        additionalWorkPrice:
          contract.additionalWorkPrice !== undefined && contract.additionalWorkPrice !== null
            ? String(contract.additionalWorkPrice)
            : '',
        billingLabel: contract.billingLabel ?? '',
        ourReference: contract.ourReference ?? '',
        vatEnabled: contract.vatEnabled ?? false,
        vatRate: contract.vatRate != null ? String(contract.vatRate) : '25'
      });
    }
  }, [isEdit, contract]);

  // Predodabir ponude kad se na ovaj ekran dolazi s detalja ponude
  // ("Kreiraj ugovor" gumb) — samo pri kreiranju, jednom pri učitavanju.
  useEffect(() => {
    if (!isEdit) {
      const offerIdParam = searchParams.get('offerId');
      if (offerIdParam) form.setFieldValue('offerId', offerIdParam);
    }
  }, []);

  const selectedOffer = useMemo(
    () => (offersData ?? []).find((offer) => offer.id === form.values.offerId),
    [offersData, form.values.offerId]
  );
  const isFromOffer = !isEdit && !!selectedOffer;
  // Klijent/cjenik ostaju zaključani ako ugovor potječe od ponude (offerId) ILI ako je
  // već REJECTED — ispravljivi su samo dok je DRAFT (korekcija pogrešno unesenih osnovnih
  // podataka), isti obrazac kao OfferFormPage (OFF-NEW-55 dopuna). Objekt NIJE u ovoj grupi —
  // tko god kreira ugovor (iz ponude ili samostalno) uvijek sam bira/upisuje VLASTITI objekt
  // (vidi ContractService.createDraftFromOffer na backendu), nikad se ne preuzima od ponude.
  const isLinkedToOffer = isEdit && !!contract?.offerId && !offerRemoved;
  const canEditClientObject = !isEdit || contract?.status === ContractStatus.DRAFT;
  // CON-09 (obrnuti smjer): kad je prijavljeni korisnik KLIJENT odabrane ponude (ne Partner),
  // ugovor se kreira preko createFromOfferByClient — klijent nema cjenik (nasljeđuje stavke iz
  // svoje ponude), ali objekt i dalje bira sam (vidi gore). Rezultat je DRAFT kao i kod
  // partnera; slanje Partneru ide tek preko "Pošalji ugovor" na detalju, nakon razrade.
  const isClientOfSelectedOffer =
    isFromOffer && !!user && user.organisationId === selectedOffer?.clientOrgId;

  // CON-04: pregled stavki/količina koje se preuzimaju iz ponude, prije spremanja ugovora —
  // isti obrazac kao ostali podaci ponude (cjenik), samo prikaz, bez uređivanja ovdje.
  const { data: selectedOfferLines } = useListOfferLines(selectedOffer?.id ?? '', {
    query: { enabled: isFromOffer }
  });

  // Odabir (ili promjena) ponude KOPIRA njezine vrijednosti u formu — OSIM objectId, koji
  // ostaje potpuno neovisan o ponudi (vidi gore). Cjenik ostaje vezan uz ponudu i prikazuje se
  // kao onemogućeno polje (vidi isFromOffer render grane niže) — naziv/opis/rok plaćanja se
  // kopiraju kao početne, i dalje editabilne vrijednosti koje partner može
  // prilagoditi prije spremanja. Broj ugovora se samo predlaže (ne
  // prepisuje već upisanu vrijednost). Uklanjanje ponude (clear na
  // dropdownu) resetira sva ova polja natrag na prazno/ručni unos.
  useEffect(() => {
    if (isEdit) return;
    if (selectedOffer) {
      form.setFieldValue('priceListId', selectedOffer.priceListId ?? '');
      form.setFieldValue('name', selectedOffer.name ?? '');
      form.setFieldValue('description', selectedOffer.description ?? '');
      form.setFieldValue('paymentTerms', String(selectedOffer.paymentTerms ?? 30));
      if (selectedOffer.offerNumber && !contractNumberTouchedRef.current) {
        form.setFieldValue('contractNumber', `${selectedOffer.offerNumber}-UG`);
      }
    } else if (!form.values.offerId) {
      form.setFieldValue('priceListId', '');
      form.setFieldValue('name', '');
      form.setFieldValue('description', '');
      form.setFieldValue('paymentTerms', '30');
      contractNumberTouchedRef.current = false;
    }
  }, [selectedOffer?.id, form.values.offerId]);

  const selectedOfferPriceListName = useMemo(() => {
    if (!selectedOffer?.priceListId) return t('createContract.noPriceList');
    return (priceListsData ?? []).find((pl) => pl.id === selectedOffer.priceListId)?.name ?? '—';
  }, [priceListsData, selectedOffer?.priceListId, t]);

  // Edit mod: naziv trenutnog cjenika prikazujemo i ako više nije aktivan
  // (nije u priceListOptions) — cjenik se ionako ne može mijenjati ovdje.
  const editPriceListName = useMemo(() => {
    if (!contract?.priceListId) return t('createContract.noPriceList');
    return (priceListsData ?? []).find((pl) => pl.id === contract.priceListId)?.name ?? '—';
  }, [priceListsData, contract?.priceListId, t]);

  // Kalendar na "Početak" ne dopušta odabir poslije već odabranog "Završetka"
  // (i obrnuto) — granica je uvijek zadnji/prvi dan tog tjedna, ne ponedjeljak,
  // da cijeli granični tjedan ostane biran (dopušten je ugovor unutar jednog tjedna).
  const startMaxDate = useMemo(() => {
    if (!form.values.endWeek || !form.values.endYear) return undefined;
    const monday = isoWeekToDate(Number(form.values.endWeek), Number(form.values.endYear));
    return monday ? new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000) : undefined;
  }, [form.values.endWeek, form.values.endYear]);

  const endMinDate = useMemo(() => {
    if (!form.values.startWeek || !form.values.startYear) return undefined;
    return isoWeekToDate(Number(form.values.startWeek), Number(form.values.startYear));
  }, [form.values.startWeek, form.values.startYear]);

  const backTarget = isEdit && id ? `/contracts/${id}` : '/contracts';

  const createMutation = useCreateContract({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        const createdId = (created as Contract | undefined)?.id;
        if (createdId) {
          navigate(`/contracts/${createdId}`, {
            state: { savedMessage: t('createContract.createSuccess') }
          });
        }
      }
    }
  });

  const createFromOfferMutation = useCreateFromOffer({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        const createdId = (created as Contract | undefined)?.id;
        if (createdId) {
          navigate(`/contracts/${createdId}`, {
            state: { savedMessage: t('createContract.createSuccess') }
          });
        }
      }
    }
  });

  // CON-09 (obrnuti smjer) — vidi isClientOfSelectedOffer.
  const createFromOfferByClientMutation = useCreateContractFromOfferByClient({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        const createdId = (created as Contract | undefined)?.id;
        if (createdId) {
          navigate(`/contracts/${createdId}`, {
            state: { savedMessage: t('createContract.createSuccess') }
          });
        }
      }
    }
  });

  const updateMutation = useUpdateContract({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        navigate(backTarget, { state: { savedMessage: t('contractFormEdit.updateSuccess') } });
      }
    }
  });

  const mutation = isEdit
    ? updateMutation
    : isClientOfSelectedOffer
      ? createFromOfferByClientMutation
      : isFromOffer
        ? createFromOfferMutation
        : createMutation;

  const handleSubmit = (values: FormValues) => {
    // startDate = ponedjeljak početnog ISO tjedna; endDate = nedjelja završnog ISO tjedna —
    // tjedan/godina su samo UI vokabular ovog FormWeekPickerInput-a, kanonski je datum (vidi
    // Contract.startDate/endDate na backendu).
    const startMonday =
      values.startWeek && values.startYear
        ? isoWeekToDate(Number(values.startWeek), Number(values.startYear))
        : undefined;
    const endMonday =
      values.endWeek && values.endYear
        ? isoWeekToDate(Number(values.endWeek), Number(values.endYear))
        : undefined;
    const startDate = startMonday ? toIsoDateString(startMonday) : undefined;
    const endDate = endMonday
      ? toIsoDateString(new Date(endMonday.getTime() + 6 * 24 * 60 * 60 * 1000))
      : undefined;
    const contractType = values.contractType as ContractContractType;
    const vatEnabled = values.vatEnabled;
    const vatRate = vatEnabled && values.vatRate ? Number(values.vatRate) : undefined;

    if (isEdit && id) {
      updateMutation.mutate({
        id,
        data: {
          clientOrgId: values.clientOrgId,
          offerId: offerRemoved ? undefined : values.offerId || undefined,
          contractNumber: values.contractNumber,
          priceListId: values.priceListId || undefined,
          objectId: values.objectId || undefined,
          name: values.name,
          description: values.description,
          startDate,
          endDate,
          paymentTerms: Number(values.paymentTerms),
          coordinator: values.coordinator || undefined,
          additionalWorkPrice: values.additionalWorkPrice
            ? Number(values.additionalWorkPrice)
            : undefined,
          billingLabel: values.billingLabel || undefined,
          ourReference: values.ourReference || undefined,
          vatEnabled,
          vatRate
        }
      });
    } else if (values.offerId && isClientOfSelectedOffer) {
      createFromOfferByClientMutation.mutate({
        offerId: values.offerId,
        data: {
          contractNumber: values.contractNumber,
          startDate,
          endDate,
          contractType,
          name: values.name,
          description: values.description,
          objectId: values.objectId || undefined,
          paymentTerms: values.paymentTerms ? Number(values.paymentTerms) : undefined
        }
      });
    } else if (values.offerId) {
      createFromOfferMutation.mutate({
        offerId: values.offerId,
        data: {
          contractNumber: values.contractNumber,
          startDate,
          endDate,
          contractType,
          name: values.name,
          description: values.description,
          priceListId: values.priceListId || undefined,
          objectId: values.objectId || undefined,
          paymentTerms: values.paymentTerms ? Number(values.paymentTerms) : undefined
        }
      });
    } else {
      createMutation.mutate({
        data: {
          clientOrgId: values.clientOrgId,
          contractNumber: values.contractNumber,
          priceListId: values.priceListId || undefined,
          objectId: values.objectId || undefined,
          name: values.name,
          description: values.description,
          startDate,
          endDate,
          contractType,
          paymentTerms: Number(values.paymentTerms),
          coordinator: values.coordinator || undefined,
          additionalWorkPrice: values.additionalWorkPrice
            ? Number(values.additionalWorkPrice)
            : undefined,
          billingLabel: values.billingLabel || undefined,
          ourReference: values.ourReference || undefined,
          vatEnabled,
          vatRate
        }
      });
    }
  };

  if (isEdit && isLoadingContract) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={backTarget} />

        <PageHeading
          title={t(isEdit ? 'contractFormEdit.title' : 'createContract.title')}
          subtitle={t(
            !isEdit
              ? 'createContract.subtitle'
              : isLinkedToOffer
                ? 'contractFormEdit.subtitleFromOffer'
                : canEditClientObject
                  ? 'contractFormEdit.subtitle'
                  : 'contractFormEdit.subtitleLocked'
          )}
        />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap={24}>
            {mutation.isError && (
              <Toast tone="danger">{contractErrorMessage(mutation.error, t('common.error'))}</Toast>
            )}

            <ContentCard>
              <Stack gap={16} p={20}>
                <Stack gap={4}>
                  <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {t('createContract.section1Title')}
                  </Text>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {t('createContract.section1Subtitle')}
                  </Text>
                </Stack>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    {isLinkedToOffer || isFromOffer || !canEditClientObject ? (
                      <TextField
                        label={t('createContract.fieldClient')}
                        disabled
                        readOnly
                        value={
                          isEdit
                            ? (contract?.clientOrgName ?? '—')
                            : (selectedOffer?.clientOrgName ?? '—')
                        }
                      />
                    ) : (
                      <SelectField
                        label={t('createContract.fieldClient')}
                        required
                        allowDeselect={false}
                        placeholder={t('createContract.fieldClientPlaceholder')}
                        data={clientOptions}
                        {...form.getInputProps('clientOrgId')}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    {isEdit ? (
                      <Stack gap={6}>
                        <TextField
                          label={t('createContract.fieldOffer')}
                          disabled
                          readOnly
                          value={
                            contract?.offerId && !offerRemoved
                              ? `#${linkedOfferForEdit?.offerNumber ?? '…'}`
                              : t('createContract.noOffer')
                          }
                        />
                        {isLinkedToOffer && (
                          <Text
                            component="a"
                            href="#"
                            onClick={(event) => {
                              event.preventDefault();
                              setRemoveOfferModalOpen(true);
                            }}
                            fz={12.5}
                            fw={600}
                            style={{ color: 'var(--color-status-danger)', cursor: 'pointer' }}
                          >
                            {t('createContract.removeOfferLink')}
                          </Text>
                        )}
                      </Stack>
                    ) : (
                      <Stack gap={6}>
                        <SelectField
                          label={t('createContract.fieldOffer')}
                          clearable
                          placeholder={t('createContract.fieldOfferPlaceholder')}
                          data={offerOptions}
                          {...form.getInputProps('offerId')}
                        />
                        <Text
                          component="a"
                          href="/offers"
                          onClick={(event) => {
                            event.preventDefault();
                            navigate('/offers');
                          }}
                          fz={12.5}
                          fw={600}
                          style={{ color: 'var(--color-text-link)', cursor: 'pointer' }}
                        >
                          {t('createContract.manageOffersLink')}
                        </Text>
                      </Stack>
                    )}
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    {isLinkedToOffer || !canEditClientObject ? (
                      <TextField
                        label={t('createContract.fieldObject')}
                        disabled
                        readOnly
                        value={
                          contract?.objectCode || contract?.objectName
                            ? formatObjectLabel(contract)
                            : t('createContract.noObject')
                        }
                      />
                    ) : (
                      <Stack gap={6}>
                        <SelectField
                          label={t('createContract.fieldObject')}
                          required
                          allowDeselect={false}
                          placeholder={t('createContract.fieldObjectPlaceholder')}
                          data={objectOptions}
                          {...form.getInputProps('objectId')}
                        />
                        <Text
                          component="a"
                          href="/templates/object-registry"
                          onClick={(event) => {
                            event.preventDefault();
                            navigate('/templates/object-registry');
                          }}
                          fz={12.5}
                          fw={600}
                          style={{ color: 'var(--color-text-link)', cursor: 'pointer' }}
                        >
                          {t('createContract.manageObjectsLink')}
                        </Text>
                      </Stack>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('createContract.fieldContractNumber')}
                      required
                      placeholder={t('createContract.fieldContractNumberPlaceholder')}
                      {...form.getInputProps('contractNumber')}
                      onChange={(event) => {
                        contractNumberTouchedRef.current = true;
                        form.getInputProps('contractNumber').onChange(event);
                      }}
                    />
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('createContract.fieldName')}
                      required
                      {...form.getInputProps('name')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <SelectField
                      label={t('createContract.fieldPaymentTerms')}
                      required
                      allowDeselect={false}
                      data={PAYMENT_TERMS_OPTIONS.map((days) => ({
                        value: days,
                        label: t('createContract.paymentTermsValue', { days })
                      }))}
                      {...form.getInputProps('paymentTerms')}
                    />
                  </div>
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    {isEdit ? (
                      <TextField
                        label={t('createContract.fieldContractType')}
                        disabled
                        readOnly
                        value={t(
                          contract?.contractType === ContractContractType.FRAMEWORK
                            ? 'createContract.contractTypeFramework'
                            : 'createContract.contractTypeFixed'
                        )}
                      />
                    ) : (
                      <SelectField
                        label={t('createContract.fieldContractType')}
                        required
                        allowDeselect={false}
                        data={[
                          {
                            value: ContractContractType.FIXED,
                            label: t('createContract.contractTypeFixed')
                          },
                          {
                            value: ContractContractType.FRAMEWORK,
                            label: t('createContract.contractTypeFramework')
                          }
                        ]}
                        helperText={t('createContract.contractTypeHelp')}
                        {...form.getInputProps('contractType')}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1 }} />
                </div>

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    <WeekPickerField
                      label={t('createContract.fieldStartDate')}
                      placeholder={t('createContract.fieldStartDatePlaceholder')}
                      week={form.values.startWeek}
                      year={form.values.startYear}
                      maxDate={startMaxDate}
                      onChange={(week, year) => {
                        form.setFieldValue('startWeek', week);
                        form.setFieldValue('startYear', year);
                        form.clearFieldError('endWeek');
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <WeekPickerField
                      label={t('createContract.fieldEndDate')}
                      placeholder={t('createContract.fieldEndDatePlaceholder')}
                      week={form.values.endWeek}
                      year={form.values.endYear}
                      minDate={endMinDate}
                      error={form.errors.endWeek}
                      onChange={(week, year) => {
                        form.setFieldValue('endWeek', week);
                        form.setFieldValue('endYear', year);
                        form.clearFieldError('endWeek');
                      }}
                    />
                  </div>
                </div>

                {!isFromOffer && (
                  <>
                    <div className="form-row-2col">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={t('createContract.fieldCoordinator')}
                          {...form.getInputProps('coordinator')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <NumberField
                          label={t('createContract.fieldAdditionalWorkPrice')}
                          helperText={t('createContract.fieldAdditionalWorkPriceHelp')}
                          allowNegative={false}
                          decimalScale={2}
                          fixedDecimalScale
                          {...form.getInputProps('additionalWorkPrice')}
                        />
                      </div>
                    </div>

                    <div className="form-row-2col">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={t('createContract.fieldBillingLabel')}
                          {...form.getInputProps('billingLabel')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={t('createContract.fieldOurReference')}
                          {...form.getInputProps('ourReference')}
                        />
                      </div>
                    </div>

                    <div className="form-row-2col">
                      <div style={{ flex: 1 }}>
                        <SelectField
                          label={t('createContract.fieldVat')}
                          allowDeselect={false}
                          data={[
                            { value: 'false', label: t('createContract.vatNo') },
                            { value: 'true', label: t('createContract.vatYes') }
                          ]}
                          value={form.values.vatEnabled ? 'true' : 'false'}
                          onChange={(value) => form.setFieldValue('vatEnabled', value === 'true')}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <NumberField
                          label={t('createContract.fieldVatRate')}
                          disabled={!form.values.vatEnabled}
                          allowNegative={false}
                          allowDecimal={false}
                          suffix=" %"
                          {...form.getInputProps('vatRate')}
                        />
                      </div>
                    </div>
                  </>
                )}

                <TextareaField
                  label={t('createContract.fieldDescription')}
                  minRows={2}
                  {...form.getInputProps('description')}
                />
              </Stack>
            </ContentCard>

            {isFromOffer && !!selectedOfferLines?.length && (
              <ContentCard>
                <Stack gap={16} p={20}>
                  <Stack gap={4}>
                    <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                      {t('createContract.offerLinesTitle')}
                    </Text>
                    <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                      {t('createContract.offerLinesSubtitle')}
                    </Text>
                  </Stack>

                  <Box style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 600 }}>
                      <div
                        className="table-header-row"
                        style={{
                          ...tableRowStyle,
                          padding: '8px 12px',
                          backgroundColor: 'var(--color-bg-canvas)',
                          borderBottom: '1px solid var(--color-border-soft)'
                        }}
                      >
                        <Text
                          fz={10}
                          fw={600}
                          style={{ width: 100, color: 'var(--color-text-secondary)' }}
                        >
                          {t('contractDetail.colCode')}
                        </Text>
                        <Text
                          fz={10}
                          fw={600}
                          style={{ flex: 1, color: 'var(--color-text-secondary)' }}
                        >
                          {t('contractDetail.colDescription')}
                        </Text>
                        <Text
                          fz={10}
                          fw={600}
                          ta="right"
                          style={{ width: 70, color: 'var(--color-text-secondary)' }}
                        >
                          {t('contractDetail.colQuantity')}
                        </Text>
                        <Text
                          fz={10}
                          fw={600}
                          ta="center"
                          style={{ width: 70, color: 'var(--color-text-secondary)' }}
                        >
                          {t('contractDetail.colUnit')}
                        </Text>
                        <Text
                          fz={10}
                          fw={600}
                          ta="right"
                          style={{ width: 110, color: 'var(--color-text-secondary)' }}
                        >
                          {t('contractDetail.colPrice')}
                        </Text>
                        <Text
                          fz={10}
                          fw={600}
                          ta="right"
                          style={{ width: 120, color: 'var(--color-text-secondary)' }}
                        >
                          {t('contractDetail.colAmount')}
                        </Text>
                      </div>
                      {selectedOfferLines.map((line) => (
                        <div
                          key={line.id}
                          style={{
                            ...tableRowStyle,
                            padding: '10px 12px',
                            borderBottom: '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text fz={13} style={{ width: 100, color: 'var(--color-text-primary)' }}>
                            {line.itemCode}
                          </Text>
                          <Text fz={13} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
                            {line.description}
                          </Text>
                          <Text
                            fz={13}
                            ta="right"
                            style={{ width: 70, color: 'var(--color-text-primary)' }}
                          >
                            {line.quantity}
                          </Text>
                          <Text
                            fz={13}
                            ta="center"
                            style={{ width: 70, color: 'var(--color-text-primary)' }}
                          >
                            {findUnitLabel(unitsData, line.unitId, i18n.language)}
                          </Text>
                          <Text
                            fz={13}
                            ta="right"
                            style={{ width: 110, color: 'var(--color-text-primary)' }}
                          >
                            {formatAmount(line.unitPrice)}
                          </Text>
                          <Text
                            fz={13}
                            fw={600}
                            ta="right"
                            style={{ width: 120, color: 'var(--color-text-primary)' }}
                          >
                            {formatAmount(line.totalPrice)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </Box>
                </Stack>
              </ContentCard>
            )}

            <ContentCard>
              <Stack gap={16} p={20}>
                <Stack gap={4}>
                  <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {t(
                      isFromOffer
                        ? 'createContract.section2TitleFromOffer'
                        : 'createContract.section2Title'
                    )}
                  </Text>
                  <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                    {t(
                      isFromOffer
                        ? 'createContract.section2SubtitleFromOffer'
                        : 'createContract.section2Subtitle'
                    )}
                  </Text>
                </Stack>

                {isLinkedToOffer || isFromOffer || !canEditClientObject ? (
                  <TextField
                    label={t(
                      isFromOffer
                        ? 'createContract.fieldPriceListFromOffer'
                        : 'createContract.fieldPriceList'
                    )}
                    disabled
                    readOnly
                    value={isEdit ? editPriceListName : selectedOfferPriceListName}
                  />
                ) : (
                  <Stack gap={6}>
                    <SelectField
                      label={t('createContract.fieldPriceList')}
                      clearable
                      placeholder={t('createContract.fieldPriceListPlaceholder')}
                      data={priceListOptions}
                      {...form.getInputProps('priceListId')}
                    />
                    <Text
                      component="a"
                      href="/templates/price-lists"
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('/templates/price-lists');
                      }}
                      fz={12.5}
                      fw={600}
                      style={{ color: 'var(--color-text-link)', cursor: 'pointer' }}
                    >
                      {t('createContract.managePriceListsLink')}
                    </Text>
                  </Stack>
                )}
              </Stack>
            </ContentCard>

            <FormActions>
              <Button variant="outline" onClick={() => navigate(backTarget)}>
                {t('common.cancel')}
              </Button>
              <MutationButton
                type="submit"
                isPending={mutation.isPending}
                isPaused={mutation.isPaused}
              >
                {t(isEdit ? 'contractFormEdit.submitButton' : 'createContract.submitButton')}
              </MutationButton>
            </FormActions>
          </Stack>
        </form>
      </Stack>

      <ConfirmActionModal
        opened={removeOfferModalOpen}
        onClose={() => setRemoveOfferModalOpen(false)}
        onConfirm={() => {
          setOfferRemoved(true);
          form.setFieldValue('clientOrgId', '');
          form.setFieldValue('objectId', '');
          form.setFieldValue('priceListId', '');
          setRemoveOfferModalOpen(false);
        }}
        title={t('createContract.removeOfferModalTitle')}
        message={t('createContract.removeOfferModalMessage')}
        confirmLabel={t('createContract.removeOfferModalConfirm')}
        danger
      />
    </Container>
  );
};
